import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  TablePagination,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import GetAppIcon from '@material-ui/icons/GetApp';
import TimelineIcon from '@material-ui/icons/Timeline';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import StatusChip from '../../components/ui/StatusChip';
import SearchBar from '../../components/ui/SearchBar';
import { exportToCsv } from '../../utils/exportCsv';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  search: { maxWidth: 320, flexGrow: 1 },
  count: { color: theme.palette.text.secondary, fontSize: '0.85rem' },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  routeCell: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.75) },
  city: { fontWeight: 600 },
  arrow: { color: '#94a3b8', fontSize: 16 },
  sub: { fontSize: '0.75rem', color: theme.palette.text.secondary },
}));

const STATUS_NORM = {
  Active: 'active',
  active: 'active',
  Inactive: 'inactive',
  inactive: 'inactive',
};

const formatDuration = (minutes) => {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h${m > 0 ? String(m).padStart(2, '0') : ''}`;
};

const formatPrice = (p) => {
  if (p == null) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(p) + ' FCFA';
};

const RoutesListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(firestore, 'routes'));
      setRoutes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching routes:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la récupération des itinéraires', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!routeToDelete) return;
    try {
      await deleteDoc(doc(firestore, 'routes', routeToDelete.id));
      setRoutes((prev) => prev.filter((r) => r.id !== routeToDelete.id));
      setSnackbar({ open: true, message: 'Itinéraire supprimé', severity: 'success' });
    } catch (err) {
      console.error('Error deleting route:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setRouteToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return routes.filter((r) => {
      const matchSearch =
        (r.name || '').toLowerCase().includes(q) ||
        (r.origin || '').toLowerCase().includes(q) ||
        (r.destination || '').toLowerCase().includes(q) ||
        (r.type || '').toLowerCase().includes(q);
      const norm = STATUS_NORM[r.status] || 'inactive';
      const matchStatus = statusFilter === 'all' || norm === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [routes, searchTerm, statusFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    exportToCsv(
      filtered,
      ['name', 'origin', 'destination', 'distance', 'duration', 'price', 'status'],
      {
        name: 'Nom',
        origin: 'Origine',
        destination: 'Destination',
        distance: 'Distance (km)',
        duration: 'Durée (min)',
        price: 'Prix',
        status: 'Statut',
      },
      'itineraires'
    );
  };

  return (
    <Box className="fade-in-up">
      <PageHeader
        breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Itinéraires' }]}
        title="Itinéraires"
        subtitle={`${filtered.length} itinéraire${filtered.length > 1 ? 's' : ''}`}
        actions={
          <>
            <Tooltip title="Exporter CSV">
              <IconButton onClick={handleExport} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <GetAppIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rafraîchir">
              <IconButton onClick={fetchRoutes} style={{ background: '#fff', border: '1px solid rgba(17,24,39,0.08)' }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/routes/create')}
            >
              Nouvel itinéraire
            </Button>
          </>
        }
      />

      <Paper>
        <Box className={classes.toolbar}>
          <Box className={classes.search}>
            <SearchBar
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0); }}
              placeholder="Rechercher par origine, destination, type…"
            />
          </Box>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="inactive">Inactif</MenuItem>
            </Select>
          </FormControl>
          <Typography className={classes.count}>
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<TimelineIcon style={{ fontSize: 36 }} />}
            title="Aucun itinéraire"
            description={
              searchTerm || statusFilter !== 'all'
                ? 'Aucun itinéraire ne correspond à vos filtres.'
                : 'Commencez par créer un premier itinéraire.'
            }
            actionLabel={!searchTerm && statusFilter === 'all' ? 'Créer un itinéraire' : undefined}
            onAction={() => navigate('/admin/routes/create')}
          />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Trajet</TableCell>
                    <TableCell>Distance</TableCell>
                    <TableCell>Durée</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Prix</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((r) => {
                    const norm = STATUS_NORM[r.status] || 'inactive';
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Box className={classes.routeCell}>
                            <Box className={classes.city}>{r.origin || '—'}</Box>
                            <ArrowForwardIcon className={classes.arrow} />
                            <Box className={classes.city}>{r.destination || '—'}</Box>
                          </Box>
                          {r.name && (
                            <Box className={classes.sub} style={{ marginTop: 2 }}>
                              {r.name}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{r.distance ? `${r.distance} km` : '—'}</TableCell>
                        <TableCell>{formatDuration(r.duration)}</TableCell>
                        <TableCell>{r.type || '—'}</TableCell>
                        <TableCell style={{ whiteSpace: 'nowrap' }}>{formatPrice(r.price)}</TableCell>
                        <TableCell>
                          <StatusChip status={norm} label={norm === 'active' ? 'Actif' : 'Inactif'} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Voir">
                            <IconButton size="small" onClick={() => navigate(`/admin/routes/${r.id}`)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Modifier">
                            <IconButton size="small" onClick={() => navigate(`/admin/routes/${r.id}/edit`)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              style={{ color: '#ef4444' }}
                              onClick={() => { setRouteToDelete(r); setOpenDeleteDialog(true); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Par page"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            />
          </>
        )}
      </Paper>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Supprimer l'itinéraire <strong>{routeToDelete?.name || `${routeToDelete?.origin} → ${routeToDelete?.destination}`}</strong> ?
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            style={{ background: '#ef4444', color: '#fff' }}
            autoFocus
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoutesListPage;
